import { User } from '../auth/User';
import {
  BulkDuplicateResult,
  CreateDealerGroupPayload,
  DealerGroup,
  UpdateDealerGroupPayload,
} from '../types/DealerGroup';
import { ApiResponse } from './ApiResponse';
import { ApiRoutes, getFullApiRoute } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';
import { IApiPaginationMetadata } from './IApiPaginationMetadata';

export class DealerGroupAPI {
  static async getAll(
    user: User | null,
    filters: {
      keyword?: string;
      page: number;
      limit: number;
      createdDateFrom?: string;
      createdDateTo?: string;
      updatedDateFrom?: string;
      updatedDateTo?: string;
      tags?: string[];
    },
  ): Promise<
    | (ApiResponse & { data: DealerGroup[]; metadata: IApiPaginationMetadata })
    | undefined
  > {
    const urlParams = new URLSearchParams({
      page: String(filters.page),
      limit: String(filters.limit),
    });
    if (filters.keyword) urlParams.append('keyword', filters.keyword);
    if (filters.createdDateFrom) urlParams.append('createdDateFrom', filters.createdDateFrom);
    if (filters.createdDateTo) urlParams.append('createdDateTo', filters.createdDateTo);
    if (filters.updatedDateFrom) urlParams.append('updatedDateFrom', filters.updatedDateFrom);
    if (filters.updatedDateTo) urlParams.append('updatedDateTo', filters.updatedDateTo);
    if (filters.tags?.length) urlParams.append('tags', filters.tags.join(','));

    const url = `${getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3)}?${urlParams.toString()}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data
      ? (result as ApiResponse & {
          data: DealerGroup[];
          metadata: IApiPaginationMetadata;
        })
      : undefined;
  }

  static async getAllTags(user: User | null): Promise<string[]> {
    const url = `${getFullApiRoute(ApiRoutes.DEALER_GROUPS)}/tags`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data ?? [];
  }

  static async getOne(user: User | null, id: string): Promise<DealerGroup | null> {
    const url = getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : null;
  }

  static async create(
    user: User | null,
    data: CreateDealerGroupPayload,
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async update(
    user: User | null,
    id: string,
    data: UpdateDealerGroupPayload,
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}`,
      'PUT',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async deleteGroup(user: User | null, id: string): Promise<boolean> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}`,
      'DELETE',
      null,
      new Headers(),
      user,
    );
    return result?.deleted ?? false;
  }

  static async duplicate(
    user: User | null,
    id: string,
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}/duplicate`,
      'POST',
      null,
      new Headers(),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async bulkDuplicate(
    user: User | null,
    groupIds: string[],
  ): Promise<BulkDuplicateResult> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/bulk-duplicate`,
      'POST',
      { groupIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return {
      requested: result?.requested ?? groupIds.length,
      duplicated: result?.duplicated ?? 0,
      failed: result?.failed ?? [],
    };
  }

  static async addDealers(
    user: User | null,
    id: string,
    dealerIds: string[],
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}/dealers`,
      'POST',
      { dealerIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async removeDealers(
    user: User | null,
    id: string,
    dealerIds: string[],
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}/dealers`,
      'DELETE',
      { dealerIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async importDealers(
    user: User | null,
    id: string,
    retailerIds: string[],
  ): Promise<DealerGroup | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.DEALER_GROUPS_V3) + `/${id}/dealers/import`,
      'POST',
      { retailerIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }
}
